// FirewallInterface.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
    RefreshCw, Network, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, ArrowLeft, Activity, Globe,
    Shield, Info, EthernetPort
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";


// ── Types ─────────────────────────────────────────────────────────────────────
interface TrafficData {
    rx_pps: number; tx_pps: number;
    rx_kbps: number; rx_mbps: number;
    tx_kbps: number; tx_mbps: number;
    error_status: string; discard_status: string;
    bandwidth_rx_mbps: number; bandwidth_tx_mbps: number;
    rx_errors_per_sec: number; tx_errors_per_sec: number;
    rx_utilization_pct: number; tx_utilization_pct: number;
    utilization_status: string;
    rx_discards_per_sec: number; tx_discards_per_sec: number;
    avg_packet_size_rx_bytes: number; avg_packet_size_tx_bytes: number;
}

interface AdditionalInfo {
    mode: string; role: string; vlan_id: number;
    secondary_ip: string[]; dhcp_relay_ip: string;
}

interface InterfaceData {
    mtu: number; name: string; type: string; vdom: string;
    zone: string; alias: string; rx_mb: number; speed: string;
    tx_mb: number; netmask: string; traffic: TrafficData;
    rx_bytes: number; tx_bytes: number; ip_address: string;
    snmp_index: number; description: string; link_status: string;
    mac_address: string; admin_status: string; interface_id: string;
    access_allowed: string; additional_info: AdditionalInfo;
}

interface FirewallInterfaceItem {
    id: number; created: string; updated: string;
    asset_id: number; agent_id: number;
    firewall_type: string; name: string; status: string | null;
    data: any;
    parsedData?: InterfaceData | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseData = (raw: any): InterfaceData | null => {
    if (typeof raw === "object" && raw !== null) {
        return raw as InterfaceData;
    }
    try { return JSON.parse(raw); } catch { return null; }
};

const fmtBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024, sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

// Convert subnet mask to CIDR length (e.g. 255.255.255.0 -> /24)
const getCidrFromNetmask = (netmask: string): string => {
    if (!netmask || netmask === "0.0.0.0") return "";

    try {
        const parts = netmask.split('.').map(Number);
        if (parts.length !== 4) return "";

        let count = 0;
        for (let i = 0; i < parts.length; i++) {
            let n = parts[i];
            while (n > 0) {
                count += n & 1;
                n >>= 1;
            }
        }
        return `/${count}`;
    } catch {
        return "";
    }
};

// ── Shared Primitives ─────────────────────────────────────────────────────────
const StatusDot = ({ status }: { status: string }) => (
    <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md gap-1 font-bold text-[9px] ${status === "up" ? "text-emerald-400 bg-emerald-400/10 border border-emerald-400/20" : "text-rose-400 bg-rose-400/10 border border-rose-400/20"}`}>
        {status.toUpperCase()}
    </span>
);

const TypeBadge = ({ type }: { type: string }) => {
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase bg-blue-500/10 border-blue-500/20 text-blue-400`}>
            {type}
        </span>
    );
};

const InlineBar = ({ value, color }: { value: number; color: string }) => (
    <div className="w-full h-1 bg-gray-700/60 rounded-full overflow-hidden mt-0.5">
        <div className="h-full rounded-full" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
    </div>
);

const renderDetailRow = (label: string, value: any, suffix: string = "", color: string = "text-slate-200") => (
    <div key={label} className="flex justify-between items-center py-1 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 px-1 -mx-1 rounded transition-colors">
        <span className={`text-[9px] font-bold ${color} uppercase tracking-widest`}>{label}</span>
        <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value ?? "")}>
            {value !== null && value !== undefined && value !== "" ? `${value}${suffix}` : "—"}
        </span>
    </div>
);


// ── Front Panel Switch View ───────────────────────────────────────────────────
const SwitchPanelView = ({ interfaces, deviceInfo, onSelect }: { interfaces: FirewallInterfaceItem[], deviceInfo: any, onSelect: (item: FirewallInterfaceItem) => void }) => {

    const [tooltipInfo, setTooltipInfo] = useState<{
        port: FirewallInterfaceItem;
        x: number;
        y: number;
        isTopRow: boolean;
    } | null>(null);

    // Create ports for the visual representation based on total ports
    const portSlots = useMemo(() => {
        let portCount = deviceInfo?.port_count || 24;
        if (deviceInfo?.is_vm) {
            portCount = 24;
        } else {
            portCount = deviceInfo?.total_ports || 24;
        }

        const slots: (FirewallInterfaceItem | null)[] = Array(portCount).fill(null);

        const physicalPorts = interfaces
            .filter(i => i.parsedData?.type?.toLowerCase() === "physical")
            .sort((a, b) => {
                const aMatch = a.name.match(/\d+$/);
                const bMatch = b.name.match(/\d+$/);
                const aNum = aMatch ? parseInt(aMatch[0], 10) : 0;
                const bNum = bMatch ? parseInt(bMatch[0], 10) : 0;
                return aNum - bNum;
            });

        // Fill available slots
        physicalPorts.forEach((port, idx) => {
            if (idx < portCount) slots[idx] = port;
        });

        return { slots, portCount };
    }, [interfaces, deviceInfo]);

    // Determine layout: single row if <= 10, otherwise group into columns of 2 (top and bottom)
    const isSingleRow = portSlots.portCount <= 10;
    const portColumns: (FirewallInterfaceItem | null)[][] = [];

    if (isSingleRow) {
        for (let i = 0; i < portSlots.portCount; i++) {
            portColumns.push([portSlots.slots[i]]);
        }
    } else {
        for (let i = 0; i < portSlots.portCount; i += 2) {
            portColumns.push([portSlots.slots[i], portSlots.slots[i + 1]]);
        }
    }

    return (
        <div>
            <div
                className="overflow-x-auto custom-scroll pb-2 hide-scrollbar"
                onScroll={() => setTooltipInfo(null)} // Hide tooltip when scrolling to avoid dangling tooltips
            >
                <div className="flex gap-1.5 justify-center min-w-max px-2">
                    <div className="border border-slate-600 rounded-xl p-2 shadow-2xl mb-4">
                        <div className="text-xs font-bold mb-1 text-blue-400">{deviceInfo?.hostname?.replace(/_/g, " ")}</div>
                        <div className="flex gap-1 border-t border-slate-600 p-2">
                            {portColumns.map((col, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-1">
                                    {col.map((port, rowIdx) => {
                                        const isTopRow = rowIdx === 0;
                                        const d = port?.parsedData;
                                        const isUp = port && d?.link_status === 'up';

                                        // Calculate port number differently based on single or double row layout
                                        const portNumber = isSingleRow ? colIdx + 1 : colIdx * 2 + rowIdx + 1;

                                        return (
                                            <div
                                                key={port ? port.id : `empty-${portNumber}`}
                                                onClick={() => port && onSelect(port)}
                                                onMouseEnter={(e) => {
                                                    if (port) {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setTooltipInfo({
                                                            port,
                                                            x: rect.left + rect.width / 2,
                                                            y: isTopRow ? rect.top : rect.bottom,
                                                            isTopRow
                                                        });
                                                    }
                                                }}
                                                onMouseLeave={() => setTooltipInfo(null)}
                                                className={`relative flex flex-col items-center p-1 -m-1 rounded-md transition-colors ${port ? 'cursor-pointer hover:bg-slate-800/60' : 'cursor-default'}`}
                                            >
                                                {/* Number above for top row */}
                                                {isTopRow && (
                                                    <span className="text-[8px] font-bold text-gray-200 mb-1.5">
                                                        {portNumber}
                                                    </span>
                                                )}

                                                {/* Port Body */}
                                                <div className={`w-8 h-8 flex items-center justify-center rounded border transition-all duration-200 relative bg-slate-950 ${isUp ? 'border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.15)]' : 'border-slate-800/80 opacity-60'}`}>
                                                    {/* Status LED */}
                                                    <div className={`absolute top-0.5 right-0.5 w-1 h-1 rounded-full ${isUp ? 'bg-emerald-500 shadow-[0_0_3px_#10b981]' : 'bg-slate-700'}`} />
                                                    {/* Port Icon */}
                                                    <EthernetPort size={16} className={`${!isTopRow ? 'rotate-180' : ''} ${isUp ? 'text-emerald-400' : 'text-gray-200'}`} />
                                                </div>

                                                {/* Number below for bottom row (won't render if it's a single row) */}
                                                {!isTopRow && (
                                                    <span className="text-[8px] font-bold text-gray-200 mt-1.5">
                                                        {portNumber}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Render Tooltip via createPortal to escape hidden overflow */}
            {tooltipInfo && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none animate-fade-in"
                    style={{
                        left: tooltipInfo.x,
                        top: tooltipInfo.isTopRow ? tooltipInfo.y - 8 : tooltipInfo.y + 8,
                        transform: `translate(-50%, ${tooltipInfo.isTopRow ? '-100%' : '0'})`
                    }}
                >
                    <div className="bg-slate-800 border border-slate-700 text-white rounded-lg shadow-xl p-2.5 w-36 flex flex-col gap-1">
                        <div className="flex justify-between items-center mb-1 border-b border-slate-700 pb-1">
                            <span className="text-[11px] font-bold text-white">{tooltipInfo.port.name}</span>
                            <div className={`w-1.5 h-1.5 rounded-full ${tooltipInfo.port.parsedData?.link_status === 'up' ? 'bg-emerald-500 shadow-[0_0_4px_#10b981]' : 'bg-rose-500'}`} />
                        </div>
                        <span className={`text-[9px] text-gray-200 truncate ${tooltipInfo.port.parsedData?.link_status === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>{tooltipInfo.port.parsedData?.link_status}</span>
                        <span className="text-[9px] text-gray-200 truncate">
                            IP: {tooltipInfo.port.parsedData?.ip_address && tooltipInfo.port.parsedData.ip_address !== "0.0.0.0"
                                ? `${tooltipInfo.port.parsedData.ip_address}${getCidrFromNetmask(tooltipInfo.port.parsedData.netmask)}`
                                : "-"}
                        </span>

                        <span className="text-[9px] text-gray-200 font-mono truncate">
                            Role: {tooltipInfo.port.parsedData?.additional_info?.role || "-"}
                        </span>

                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};


// ── Detail View ───────────────────────────────────────────────────────────────
function InterfaceDetailView({ item, onBack }: { item: FirewallInterfaceItem; onBack: () => void }) {
    const d = item.parsedData || parseData(item.data);

    return (
        <div className="space-y-3 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 bg-gray-800/40 border border-gray-700 p-2.5 rounded-xl">
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={onBack}
                        className="p-1.5 bg-blue-500/10 border border-blue-500/30 rounded-md text-blue-400 hover:text-white hover:bg-blue-500/20 transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div className="bg-blue-500/10 p-1.5 rounded-md border border-blue-500/20">
                        <Network className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-white leading-none">{item.name}</h2>
                            {d && <StatusDot status={d.link_status} />}
                            {d && <TypeBadge type={d.type} />}
                        </div>
                        {d?.alias && <p className="text-[10px] text-gray-200 mt-0.5">{d.alias}</p>}
                    </div>
                </div>
                {d?.ip_address && d.ip_address !== "0.0.0.0" && (
                    <span className="font-mono text-[11px] text-cyan-300/80">{d.ip_address}{getCidrFromNetmask(d.netmask)}</span>
                )}
            </div>

            {!d ? (
                <p className="text-gray-200 text-xs p-4">Failed to parse interface data.</p>
            ) : (
                <>
                    {/* Row 1: Identity + Network + Status */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Identity */}
                        <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                                <Info className="w-3.5 h-3.5 text-blue-400" />
                                <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Identity</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Name", d.name)}
                                {renderDetailRow("Alias", d.alias)}
                                {renderDetailRow("Type", d.type)}
                                {renderDetailRow("VDOM", d.vdom)}
                                {renderDetailRow("Zone", d.zone)}
                                {renderDetailRow("SNMP Index", d.snmp_index)}
                            </div>
                        </div>

                        {/* Network */}
                        <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                                <Globe className="w-3.5 h-3.5 text-cyan-400" />
                                <h4 className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">Network</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("IP Address", d.ip_address !== "0.0.0.0" ? d.ip_address : null)}
                                {renderDetailRow("Netmask", d.netmask !== "0.0.0.0" ? d.netmask : null)}
                                {renderDetailRow("MAC Address", d.mac_address)}
                                {renderDetailRow("MTU", d.mtu)}
                                {renderDetailRow("Speed", d.speed)}
                                {renderDetailRow("Mode", d.additional_info?.mode)}
                                {renderDetailRow("VLAN ID", d.additional_info?.vlan_id)}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                                <Activity className="w-3.5 h-3.5 text-green-400" />
                                <h4 className="text-[10px] font-black text-green-400 uppercase tracking-widest">Status & Access</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Link Status", d.link_status.toUpperCase(), "", d.link_status === "up" ? "text-emerald-400" : "text-rose-400")}
                                {renderDetailRow("Admin Status", d.admin_status.toUpperCase(), "", d.admin_status === "up" ? "text-emerald-400" : "text-rose-400")}
                                {renderDetailRow("Role", d.additional_info?.role !== "undefined" ? d.additional_info?.role : null)}
                                {renderDetailRow("DHCP Relay", d.additional_info?.dhcp_relay_ip)}
                            </div>
                            {d.access_allowed && (
                                <div className="mt-3">
                                    <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest block mb-1.5">Access Allowed</span>
                                    <div className="flex flex-wrap gap-1">
                                        {d.access_allowed.split(" ").map((a) => (
                                            <span key={a} className="px-1.5 py-0.5 text-[8px] font-bold bg-gray-700/60 border border-gray-600/40 rounded text-gray-300 uppercase">
                                                {a}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Traffic RX + TX */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* RX */}
                        <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                                <ArrowDown className="w-3.5 h-3.5 text-blue-400" />
                                <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Inbound (RX)</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Total RX", fmtBytes(d.rx_bytes))}
                                {renderDetailRow("Speed", `${d.traffic.rx_mbps} Mbps`)}
                                {renderDetailRow("PPS", d.traffic.rx_pps)}
                                {renderDetailRow("kbps", d.traffic.rx_kbps)}
                                {renderDetailRow("Bandwidth", `${d.traffic.bandwidth_rx_mbps} Mbps`)}
                                {renderDetailRow("Errors/s", d.traffic.rx_errors_per_sec, "", d.traffic.rx_errors_per_sec > 0 ? "text-rose-400" : "text-slate-200")}
                                {renderDetailRow("Discards/s", d.traffic.rx_discards_per_sec, "", d.traffic.rx_discards_per_sec > 0 ? "text-rose-400" : "text-slate-200")}
                                {renderDetailRow("Avg Packet", `${d.traffic.avg_packet_size_rx_bytes.toFixed(1)} B`)}
                                <div className="pt-1">
                                    <div className="flex justify-between mb-0.5">
                                        <span className="text-[9px] text-gray-200 uppercase tracking-widest font-bold">Utilization</span>
                                        <span className="text-[9px] text-blue-400 font-bold">{d.traffic.rx_utilization_pct}%</span>
                                    </div>
                                    <InlineBar value={d.traffic.rx_utilization_pct} color="#3b82f6" />
                                </div>
                            </div>
                        </div>

                        {/* TX */}
                        <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                                <ArrowUp className="w-3.5 h-3.5 text-orange-400" />
                                <h4 className="text-[10px] font-black text-orange-400 uppercase tracking-widest">Outbound (TX)</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Total TX", fmtBytes(d.tx_bytes))}
                                {renderDetailRow("Speed", `${d.traffic.tx_mbps} Mbps`)}
                                {renderDetailRow("PPS", d.traffic.tx_pps)}
                                {renderDetailRow("kbps", d.traffic.tx_kbps)}
                                {renderDetailRow("Bandwidth", `${d.traffic.bandwidth_tx_mbps} Mbps`)}
                                {renderDetailRow("Errors/s", d.traffic.tx_errors_per_sec, "", d.traffic.tx_errors_per_sec > 0 ? "text-rose-400" : "text-slate-200")}
                                {renderDetailRow("Discards/s", d.traffic.tx_discards_per_sec, "", d.traffic.tx_discards_per_sec > 0 ? "text-rose-400" : "text-slate-200")}
                                {renderDetailRow("Avg Packet", `${d.traffic.avg_packet_size_tx_bytes.toFixed(1)} B`)}
                                <div className="pt-1">
                                    <div className="flex justify-between mb-0.5">
                                        <span className="text-[9px] text-gray-200 uppercase tracking-widest font-bold">Utilization</span>
                                        <span className="text-[9px] text-orange-400 font-bold">{d.traffic.tx_utilization_pct}%</span>
                                    </div>
                                    <InlineBar value={d.traffic.tx_utilization_pct} color="#f97316" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Health + Interface ID */}
                    <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                            <Shield className="w-3.5 h-3.5 text-gray-200" />
                            <h4 className="text-[10px] font-black text-gray-200 uppercase tracking-widest">Health & Metadata</h4>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-0.5">
                            {renderDetailRow("Error Status", d.traffic.error_status, "", d.traffic.error_status === "Normal" ? "text-emerald-400" : "text-rose-400")}
                            {renderDetailRow("Discard Status", d.traffic.discard_status, "", d.traffic.discard_status === "Normal" ? "text-emerald-400" : "text-rose-400")}
                            {renderDetailRow("Utilization Status", d.traffic.utilization_status, "", d.traffic.utilization_status === "Normal" ? "text-emerald-400" : "text-rose-400")}
                        </div>
                        <div className="mt-3">
                            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">Interface ID</span>
                            <p className="font-mono text-[10px] text-gray-200 break-all mt-0.5">{d.interface_id}</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallInterface({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [interfaces, setInterfaces] = useState<FirewallInterfaceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [deviceInfo, setDeviceInfo] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<FirewallInterfaceItem | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // New Type Filter State
    const [activeType, setActiveType] = useState<string>("All");

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchInterfaces = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) {
                queryObj.$or = [{ name: { $ilike: `%${debouncedSearch}%` } }];
            }
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            const res = await baseapi.get("/firewall_interfaces", { params });
            if (res.data.status) {
                const parsedInterfaces = res.data.data.map((item: any) => ({
                    ...item,
                    parsedData: parseData(item.data)
                }));
                let parsedDevInfo = res.data.device_info;
                if (typeof parsedDevInfo === "string") {
                    try {
                        parsedDevInfo = JSON.parse(parsedDevInfo);
                    } catch {
                        parsedDevInfo = {};
                    }
                }
                setDeviceInfo(parsedDevInfo || {});
                setInterfaces(parsedInterfaces);
                setTotalRecords(res.data.total ?? parsedInterfaces.length);
            } else {
                showError("Failed to load interfaces");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [asset_id, debouncedSearch, currentPage, limit]);

    useEffect(() => {
        if (!selected) fetchInterfaces();
    }, [fetchInterfaces, selected]);

    const allTypes = useMemo(() => {
        const typesSet = new Set<string>();
        interfaces.forEach(item => {
            if (item.parsedData?.type) {
                typesSet.add(item.parsedData.type);
            }
        });
        return ["All", ...Array.from(typesSet).sort()];
    }, [interfaces]);

    const filteredInterfaces = useMemo(() => {
        if (activeType === "All") return interfaces;
        return interfaces.filter(item => item.parsedData?.type === activeType);
    }, [interfaces, activeType]);

    const displayTotal = activeType === "All" ? totalRecords : filteredInterfaces.length;
    const totalPages = Math.ceil(displayTotal / limit);
    const startRecord = displayTotal === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, displayTotal);


    // ── Detail View ────────────────────────────────────────────────────────────
    if (selected) {
        return <InterfaceDetailView item={selected} onBack={() => setSelected(null)} />;
    }

    // ── Table View ─────────────────────────────────────────────────────────────
    const Th = ({ label, right = false }: { label: string; right?: boolean }) => (
        <th className={`px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}>
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label}
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-1">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <Network size={16} className="text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Interfaces</h1>
                </div>
            </div>

            {/* Switch Panel View - only visible when "All" or "Physical" is selected */}
            {!loading && (activeType === "All" || activeType.toLowerCase() === "physical") && (
                <SwitchPanelView interfaces={interfaces} deviceInfo={deviceInfo} onSelect={setSelected} />
            )}

            {/* Table Area Container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">

                {/* Search & Filter Header INSIDE the Table Container */}
                <div className="px-4 py-3 border-b border-gray-700 flex flex-wrap items-center justify-between gap-4 bg-gray-800/60">
                    {/* Tabs Filter */}
                    {!loading && allTypes.length > 1 && (
                        <div className="flex items-center gap-2 overflow-x-auto flex-nowrap hide-scrollbar">
                            {allTypes.map(type => {
                                const isActive = activeType === type;
                                return (
                                    <button
                                        key={type}
                                        onClick={() => {
                                            setActiveType(type);
                                            setCurrentPage(1);
                                        }}
                                        className={`px-3 py-1.5 text-[10px] uppercase font-bold rounded-full transition-all border whitespace-nowrap flex items-center gap-1.5
                                            ${isActive
                                                ? "bg-emerald-600/20 text-emerald-400 border-emerald-500/30"
                                                : "bg-gray-800/40 text-gray-200 border-gray-700 hover:bg-gray-700/50 hover:text-gray-200"}`}
                                    >
                                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-gray-500"}`}></span>
                                        {type}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className="flex items-center gap-2 ml-auto">
                        <div className="relative w-56 group">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-blue-400 transition-colors" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search interfaces..."
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                            />
                        </div>
                        <button
                            onClick={fetchInterfaces}
                            disabled={loading}
                            className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                        >
                            <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <Th label="Interface" />
                                <Th label="Type" />
                                <Th label="IP Address" />
                                <Th label="Link" />
                                <Th label="RX" right />
                                <Th label="TX" right />
                                <Th label="RX Util" right />
                                <Th label="TX Util" right />
                                <Th label="Access" />
                                <Th label="Role" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-200">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading interfaces...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredInterfaces.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Network size={24} className="text-gray-200" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No interfaces found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredInterfaces.slice((currentPage - 1) * limit, currentPage * limit).map((item) => {
                                    const d = item.parsedData;
                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view details"
                                        >
                                            {/* Interface */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="text-[11px] font-bold text-gray-100">{item.name}</p>
                                                        {d?.alias && <p className="text-[9px] text-gray-200">{d.alias}</p>}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Type */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                {d ? <TypeBadge type={d.type} /> : <span className="text-gray-200">—</span>}
                                            </td>

                                            {/* IP */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="font-mono text-[11px] text-cyan-300/80">
                                                    {d?.ip_address && d.ip_address !== "0.0.0.0"
                                                        ? `${d.ip_address}${getCidrFromNetmask(d.netmask)}`
                                                        : "—"}
                                                </span>
                                            </td>

                                            {/* Link */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                {d ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <StatusDot status={d.link_status} />
                                                        {d.admin_status !== d.link_status && (
                                                            <span className="text-[9px] text-gray-200">adm: {d.admin_status}</span>
                                                        )}
                                                    </div>
                                                ) : "—"}
                                            </td>

                                            {/* RX */}
                                            <td className="px-4 py-2 whitespace-nowrap text-right">
                                                <div className="flex flex-col items-end gap-0.5">
                                                    <span className="text-[11px] font-mono text-blue-300">
                                                        {d ? fmtBytes(d.rx_bytes) : "—"}
                                                    </span>
                                                    {d && d.traffic.rx_mbps > 0 && (
                                                        <span className="text-[9px] text-gray-200">{d.traffic.rx_mbps} Mbps</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* TX */}
                                            <td className="px-4 py-2 whitespace-nowrap text-right">
                                                <div className="flex flex-col items-end gap-0.5">
                                                    <span className="text-[11px] font-mono text-orange-300">
                                                        {d ? fmtBytes(d.tx_bytes) : "—"}
                                                    </span>
                                                    {d && d.traffic.tx_mbps > 0 && (
                                                        <span className="text-[9px] text-gray-200">{d.traffic.tx_mbps} Mbps</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* RX Util */}
                                            <td className="px-4 py-2 whitespace-nowrap text-right">
                                                {d && d.traffic.rx_utilization_pct > 0 ? (
                                                    <div className="w-20 ml-auto">
                                                        <div className="flex justify-between">
                                                            <span className="text-[9px] text-blue-400 font-bold">{d.traffic.rx_utilization_pct}%</span>
                                                        </div>
                                                        <InlineBar value={d.traffic.rx_utilization_pct} color="#3b82f6" />
                                                    </div>
                                                ) : <span className="text-gray-200 text-[10px]">—</span>}
                                            </td>

                                            {/* TX Util */}
                                            <td className="px-4 py-2 whitespace-nowrap text-right">
                                                {d && d.traffic.tx_utilization_pct > 0 ? (
                                                    <div className="w-20 ml-auto">
                                                        <div className="flex justify-between">
                                                            <span className="text-[9px] text-orange-400 font-bold">{d.traffic.tx_utilization_pct}%</span>
                                                        </div>
                                                        <InlineBar value={d.traffic.tx_utilization_pct} color="#f97316" />
                                                    </div>
                                                ) : <span className="text-gray-200 text-[10px]">—</span>}
                                            </td>

                                            {/* Access */}
                                            <td className="px-4 py-2">
                                                {d?.access_allowed ? (
                                                    <div className="flex flex-wrap gap-1 max-w-[140px]">
                                                        {d.access_allowed.split(" ").map((a) => (
                                                            <span key={a} className="px-1 py-0.5 text-[8px] font-bold bg-gray-700/60 border border-gray-600/40 rounded text-gray-300 uppercase">
                                                                {a}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-gray-200">—</span>}
                                            </td>

                                            {/* Role */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                {d?.additional_info?.role && d.additional_info.role !== "undefined" ? (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border bg-orange-500/10 border-orange-500/20 text-orange-400 uppercase">
                                                        {d.additional_info.role}
                                                    </span>
                                                ) : <span className="text-gray-200">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{displayTotal}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}